import React from "react";
import copy from "copy-to-clipboard";
import { toast } from "react-hot-toast";
import { FiShare2, FiChevronRight } from "react-icons/fi";
import { BsShieldCheck } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { addToCart } from "../../../slices/cartSlice";
import { ACCOUNT_TYPE } from "../../../utils/constants";
import { DS } from "../../../utils/designSystem";

function CourseDetailsCard({ course, setConfirmationModal, handleBuyCourse }) {
  const { user }  = useSelector((s) => s.profile);
  const { token } = useSelector((s) => s.auth);
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const { thumbnail, price, courseName, studentsEnrolled, instructions } = course;
  const isEnrolled = user && studentsEnrolled?.includes(user._id);

  const handleAddToCart = () => {
    if (user?.accountType === ACCOUNT_TYPE.INSTRUCTOR) {
      toast.error("Instructors cannot purchase courses.");
      return;
    }
    if (token) {
      dispatch(addToCart(course));
      return;
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to add to cart.",
      btn1Text: "Log in",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  const handleShare = () => {
    copy(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-richblack-700 bg-richblack-800 shadow-[0_8px_40px_rgba(0,0,8,0.6)]">
      {/* Thumbnail */}
      <div className="aspect-video w-full overflow-hidden bg-richblack-700">
        <img
          src={thumbnail}
          alt={courseName}
          loading="eager"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Card body */}
      <div className="flex flex-col gap-4 p-5">
        {/* Price */}
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-richblack-5">
            ₹{price?.toLocaleString("en-IN")}
          </span>
          <span className="text-sm font-normal text-richblack-400">/-</span>
        </div>

        {/* CTAs */}
        <button
          onClick={isEnrolled ? () => navigate("/dashboard/enrolled-courses") : handleBuyCourse}
          className={`${DS.btn.primary} w-full justify-center`}
        >
          {isEnrolled ? "Go To Course" : "Buy Now"}
        </button>

        {!isEnrolled && (
          <button
            onClick={handleAddToCart}
            className={`${DS.btn.secondary} w-full justify-center`}
          >
            Add to Cart
          </button>
        )}

        {/* Guarantee */}
        <div className="flex items-center justify-center gap-2 rounded-xl border border-richblack-700 bg-richblack-900/40 px-4 py-3 text-xs text-richblack-300">
          <BsShieldCheck className="flex-shrink-0 text-base text-caribbeangreen-100" />
          <span>30-Day Money-Back Guarantee · 180-day access</span>
        </div>

        {/* Requirements / Instructions */}
        {instructions?.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-semibold text-richblack-5">
              Requirements
            </p>
            <ul className="flex flex-col gap-2">
              {instructions.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-richblack-300">
                  <FiChevronRight className="mt-0.5 flex-shrink-0 text-yellow-50" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-richblack-700" />

        {/* Share */}
        <button
          onClick={handleShare}
          className="flex items-center justify-center gap-2 text-sm text-richblack-300 transition-colors hover:text-richblack-5"
        >
          <FiShare2 className="text-base" />
          Share this course
        </button>
      </div>
    </div>
  );
}

export default CourseDetailsCard;
